import { useMemo } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';

export type BadgeSeverity = 'critical' | 'warning' | 'info';
export interface BadgeValue {
  count: number;
  severity: BadgeSeverity;
}

interface Page<T> {
  items: T[];
}
const up = (s: string | null | undefined): string => (s ?? '').toUpperCase();

/**
 * Live counts for the nav badges (Phase 2.6).
 *
 * Only attention-worthy, actionable signals get a badge (per the audit / brief §16):
 * active alerts, drifted resources, failed deployments, pending federation
 * invitations — never vanity totals. Each fetch is permission-gated so it never runs
 * (or leaks) for a user who cannot read that resource, and fails soft: a failed or
 * empty request simply yields no badge. Keyed by the `badge` id in the nav registry.
 */
export function useBadges(): Record<string, BadgeValue | undefined> {
  const { can } = useAuth();

  const alerts = useAsync(
    () =>
      can('alert:read')
        ? api.get<Page<{ severity: string; status: string }>>('/alerts?limit=50')
        : Promise.resolve(null),
    [],
  );
  const drift = useAsync(
    () => (can('deployment:read') ? api.get<unknown[]>('/drift') : Promise.resolve(null)),
    [],
  );
  const deployments = useAsync(
    () =>
      can('deployment:read')
        ? api.get<Page<{ status: string }>>('/deployments?limit=50')
        : Promise.resolve(null),
    [],
  );
  const invitations = useAsync(
    () =>
      can('federation:read')
        ? api.get<Array<{ direction: string; status: string }>>('/federation/invitations')
        : Promise.resolve(null),
    [],
  );

  return useMemo(() => {
    const out: Record<string, BadgeValue | undefined> = {};

    const activeAlerts = (alerts.data?.items ?? []).filter(
      (a) => up(a.status) !== 'RESOLVED' && up(a.status) !== 'CLOSED',
    );
    if (activeAlerts.length > 0) {
      const critical = activeAlerts.some((a) => up(a.severity) === 'CRITICAL');
      out.alerts = { count: activeAlerts.length, severity: critical ? 'critical' : 'warning' };
    }

    const driftCount = Array.isArray(drift.data) ? drift.data.length : 0;
    if (driftCount > 0) out.drift = { count: driftCount, severity: 'warning' };

    const failed = (deployments.data?.items ?? []).filter((d) => up(d.status) === 'FAILED');
    if (failed.length > 0) out.deployments = { count: failed.length, severity: 'critical' };

    const pending = (invitations.data ?? []).filter(
      (i) => up(i.direction) === 'INCOMING' && ['SENT', 'VIEWED'].includes(up(i.status)),
    );
    if (pending.length > 0) out.federation = { count: pending.length, severity: 'info' };

    return out;
  }, [alerts.data, drift.data, deployments.data, invitations.data]);
}
